import type { MandateNotEvaluableReason, RiskBoundNotEvaluableReason } from "@repo/shared/types";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./compliance-summary-card.i18n";

/**
 * QL-313 — the summary card of a goal-level compliance chart (volatility,
 * equity share): the status badge, the year-1 figure against the ceiling, the
 * follow-up baseline, the "risk model moved since the decision" note on a
 * locked instance, the coverage warning for weight the measure could not see,
 * and the breach message. One card for both charts, as the decision card is,
 * so the two cannot drift. The card owns its fixed words (sidecar); the caller
 * passes only the measure-specific text through `labels`. Status
 * colour goes through the Badge variants and the status tokens (DESIGN.md:
 * the icon carries the colour, the text stays foreground).
 */

export type ComplianceEffectiveStatus = "ok" | "accepted" | "not_accepted" | "deviation";

/** The measure-specific text; the card owns every word that is the same for all measures. */
export type ComplianceSummaryCardLabels = {
  year1Label: string;
  baselineLabel?: string;
  /** `{pct}` is replaced with the rounded unknown share. */
  coverageWarning: (pct: string) => string;
  /** The bare fact of the breach; the card adds the "what to do next" line. */
  breachMessage: string;
  /** QL-493 — the not-evaluable causes; optional so informational callers need not pass them. */
  notEvaluableNoRiskScore?: string;
  notEvaluableNoSnapshot?: string;
  notEvaluableMissingAssetData?: string;
  /** QL-375 — the mandate's fifth reason: the goal type requires a mandate and none resolved. */
  notEvaluableNoMandateRequired?: string;
};

type ComplianceSummaryCardProps = {
  title: string;
  labels: ComplianceSummaryCardLabels;
  effective: ComplianceEffectiveStatus | null;
  /**
   * QL-493 — set when a ceiling applies and the goal could not be evaluated
   * (`effective` is null). Rendered as an amber badge plus the cause; no
   * decision can be taken in this state.
   */
  notEvaluableReason?: RiskBoundNotEvaluableReason | MandateNotEvaluableReason | null;
  /** The gating figure, percent. Null when the snapshot has no year-1 point. */
  year1Pct: number | null;
  /** The ceiling in force, percent. Null = no cap (informational chart). */
  ceilingPct: number | null;
  /** The existing portfolio's figure on a follow-up; omitted otherwise. */
  baselinePct?: number | null;
  /** Set on a locked instance whose risk model moved after the decision. */
  ceilingChange?: { from: number; to: number | null } | null;
  /** The share the measure could not see, percent; 0 hides the warning. */
  unknownPct: number;
  deviationAcceptanceEnabled: boolean;
};

export function ComplianceSummaryCard({
  title,
  labels,
  effective,
  year1Pct,
  ceilingPct,
  baselinePct,
  ceilingChange,
  unknownPct,
  deviationAcceptanceEnabled,
  notEvaluableReason,
}: ComplianceSummaryCardProps) {
  const t = useT(T);
  const formatPct = (v: number | null) => (v == null ? t.noCap : `${v.toFixed(1)}%`);
  const withinOrAccepted = effective === "ok" || effective === "accepted";
  const notEvaluable = effective === null && notEvaluableReason != null;
  const notEvaluableText =
    notEvaluableReason === "no_risk_score"
      ? labels.notEvaluableNoRiskScore
      : notEvaluableReason === "no_snapshot"
        ? labels.notEvaluableNoSnapshot
        : notEvaluableReason === "missing_asset_data"
          ? labels.notEvaluableMissingAssetData
          : notEvaluableReason === "no_mandate_required"
            ? labels.notEvaluableNoMandateRequired
            : undefined;

  const badge = notEvaluable ? (
    <Badge variant="warning">{t.statusNotEvaluable}</Badge>
  ) : effective === "ok" ? (
    <Badge variant="success">{t.statusOk}</Badge>
  ) : effective === "accepted" ? (
    <Badge variant="warning">{t.statusAccepted}</Badge>
  ) : effective === "not_accepted" ? (
    <Badge variant="destructive">{t.statusNotAccepted}</Badge>
  ) : effective === "deviation" ? (
    <Badge variant="destructive">{t.statusDeviation}</Badge>
  ) : null;

  return (
    <div className="space-y-3 border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <span className="font-medium">{title}</span>
        {badge}
      </div>

      {notEvaluable && (
        <div className="flex items-start gap-2 text-sm text-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{notEvaluableText ?? t.statusNotEvaluable}</span>
        </div>
      )}

      {year1Pct != null && !notEvaluable && (
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            {withinOrAccepted ? (
              <CheckCircle2 className="h-4 w-4 text-success" />
            ) : (
              <XCircle className="h-4 w-4 text-destructive" />
            )}
            {labels.year1Label}
          </span>
          <span className="tabular-nums">
            {t.actual} {formatPct(year1Pct)} · {t.max} {formatPct(ceilingPct)}
          </span>
        </div>
      )}

      {baselinePct != null && labels.baselineLabel && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{labels.baselineLabel}</span>
          <span className="tabular-nums">{formatPct(baselinePct)}</span>
        </div>
      )}

      {ceilingChange && (
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            {ceilingChange.to != null
              ? t.ceilingChangedTo(ceilingChange.to.toFixed(1))
              : t.ceilingDisabledSince}
          </span>
        </div>
      )}

      {unknownPct > 0 && (
        <div className="flex items-start gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning" />
          <span>{labels.coverageWarning(Math.round(unknownPct).toString())}</span>
        </div>
      )}

      {(effective === "deviation" || effective === "not_accepted") && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            {labels.breachMessage}{" "}
            {!deviationAcceptanceEnabled
              ? t.hardBlockMessage
              : effective === "not_accepted"
                ? t.breachBlockedHint
                : t.breachAcceptHint}
          </span>
        </div>
      )}
    </div>
  );
}
